/**
 * 照片识别没过时，主人主动再交一次算不算新的一次（2531 rc1900 建宠卡住，I 10-03 18:5x 转；A 核过后端口径；文案 P 19:0x 确认）。
 * 建宠物页、“TA 的样子”页补照片、换照片共用：
 * - photoRejectedForSure：后端明确拒了、回执完整、这次没存下任何东西的确定终态——主人主动点“重新识别”就是一次新的识别操作，换新幂等键
 *   （识别对同一张照片可能这次没认出来、下次认得出：P 实测同一张猫照下午三次过、晚上两次没过）。
 *   不在这里、沿用原键的（c84a 1046Z D1046-2、D1046-5）：503 recognition_unavailable（在途、发送与费用不明，不能靠换键绕过 unknown）；
 *   断网、超时、双击、刷新恢复（同一次提交的重放，后端照幂等认出来，不会多建一只、多存一张）。
 * - 结果不明照实说“还没确认”，保留原操作，不把再次写入包装成查询（c84a 1116Z D1116-2 第 5 条：不能按钮写重试却永久重复旧结论，
 *   也不能背后循环）。服务端给出恢复动作（CR-RECOG-TIMEOUT-1）以后再按合同显示。
 * - 没配好（UI-PHOTO-RECOVERY-RETRYABLE-01，I 10-14；后端 P-RECOG-RECOVERY-BEFORE-SEND-01）：识别开关关着、识别器没装、灰度没有来源时，
 *   后端同样标 before_send（确实没发出去），但这个环境里再试多少次都一样，所以 retryable=false。单列成 not_ready：照说后端那句
 *   （兜底「现在还放不了照片，暂时没法带 TA 进来。」），不给再试、不换新键。急停、missing_budget 也是 before_send，但可重试，照旧。
 * - 说法：没认出来把原因放在我们这边（不说主人拍糊了）；同一张连着两次没认出来，把重点放到换一张上（P 19:0x 建议）。
 */
import type { ApiError } from "@/shared/api/errors";

export const PHOTO_UNCLEAR_TEXT = "这张这会儿没认出来，可以再试一次，或者换一张 TA 正脸、亮一点的。";
export const PHOTO_UNCLEAR_AGAIN_TEXT = "这张试了两次还没认出来，换一张 TA 正脸、亮一点的会更快。";
export const RECOGNITION_PENDING_TEXT = "这张照片的识别结果还没确认，可以稍后查看结果，或者换一张照片。";
/** Legacy display export retained for compatibility; current consumers do not use it to offer a result GET. */
export const RECOGNITION_PENDING_ACTION = "查看结果";
/** 没配好（not_ready）时后端原话不能直接给玩家看的兜底，和后端 NOT_SET_UP_MESSAGE 同一句 */
export const PHOTO_NOT_READY_TEXT = "现在还放不了照片，暂时没法带 TA 进来。";

export type PhotoRecoveryPhase = "before_send" | "not_ready" | "rejected" | "unknown" | "other";
const MEDIA_REJECTION_REASONS = new Set(["empty", "too_large", "unsupported_type", "type_mismatch", "decode_failed", "too_many_pixels", "too_small", "animated_not_supported", "photo_not_pet", "species_not_supported", "species_mismatch", "photo_unclear"]);
/** Source-bound envelope consistency only. Never an authority or result-query permission. */
export function photoRecoveryPhase(error: ApiError): PhotoRecoveryPhase {
  const reason = error.details?.reason;
  const phase = error.details?.recovery;
  const hasPhase = error.details !== null && Object.prototype.hasOwnProperty.call(error.details, "recovery");
  // Explicit unknown and transport failure always win over a conflicting code/phase.
  if (phase === "result_unknown" || error.kind === "network" || error.kind === "timeout" || error.kind === "aborted") return "unknown";
  if (error.kind !== "http") return hasPhase || error.code === "MEDIA_REJECTED" || error.code === "VALIDATION_FAILED" ? "unknown" : "other";
  const rateLimited = error.status === 429 && error.code === "RATE_LIMITED" && reason === "recognition_rate_limited";
  const nativeBeforeSend = error.status === 503 && error.code === "UPSTREAM_UNAVAILABLE" && reason === "recognition_unavailable" && phase === "before_send";
  const unclear = error.status === 422 && error.code === "MEDIA_REJECTED" && reason === "photo_unclear";
  if (hasPhase) {
    // 明确没发出去还要看能不能再试：没配好时后端标 retryable=false，再试也一样，单列（UI-PHOTO-RECOVERY-RETRYABLE-01）
    if (nativeBeforeSend) return error.retryable !== false ? "before_send" : "not_ready";
    if (phase === "before_send" && rateLimited) return "rejected";
    if (phase === "terminal_unclear" && unclear) return "rejected";
    return "unknown";
  }
  if (error.status !== null && error.status >= 500 && !error.isCapabilityUnavailable) return "unknown";
  if (rateLimited) return "rejected";
  if (error.status === 422 && error.code === "VALIDATION_FAILED") return "rejected";
  if (error.status === 422 && error.code === "MEDIA_REJECTED" && typeof reason === "string" && MEDIA_REJECTION_REASONS.has(reason)) return "rejected";
  // A photo code/reason with no complete native tuple is not a confirmed denial.
  if (error.code === "MEDIA_REJECTED" || error.code === "VALIDATION_FAILED" || reason === "recognition_unavailable" || reason === "recognition_rate_limited" || (typeof reason === "string" && MEDIA_REJECTION_REASONS.has(reason))) return "unknown";
  return "other";
}
export function photoBeforeSend(error: ApiError): boolean { return photoRecoveryPhase(error) === "before_send"; }
export function photoResultUncertain(error: ApiError): boolean { return photoRecoveryPhase(error) === "unknown"; }
export function photoRejectedForSure(error: ApiError): boolean { return photoRecoveryPhase(error) === "rejected"; }
/** 没配好：明确没发出去，但这个环境里再试也一样。不给再试、不换新键 */
export function photoNotReady(error: ApiError): boolean { return photoRecoveryPhase(error) === "not_ready"; }
/** 没配好时说的那一句：后端原话能直接给玩家看（playerMessage 就是原话）就用原话，否则用兜底——不落到「过一会儿再试一次」那类按状态的套话 */
export function photoNotReadyText(error: ApiError): string {
  return error.message.trim() && error.playerMessage === error.message ? error.message : PHOTO_NOT_READY_TEXT;
}

/** Compatibility reason-family predicate only; this does not authorize replay or result GET. */
export function isRecognitionPending(error: ApiError): boolean {
  return error.code === "UPSTREAM_UNAVAILABLE" && error.details?.reason === "recognition_unavailable";
}
export function isPhotoUnclear(error: ApiError): boolean {
  return photoRejectedForSure(error) && error.code === "MEDIA_REJECTED" && error.details?.reason === "photo_unclear";
}

/** 没认出来那一句：同一张照片第几次没认出来（换了照片从 0 数起） */
export function photoUnclearText(times: number): string {
  return times >= 2 ? PHOTO_UNCLEAR_AGAIN_TEXT : PHOTO_UNCLEAR_TEXT;
}
